// ExerciseStatusBadges — 2 badges derivadas del ExerciseSchema:
//   - "Calibración preliminar" (amber) cuando clinical_review_status === "preliminary"
//   - "Sin detección de cámara" (slate/gris) cuando tracking_mode === "video_only"
//
// Sprint 3 PR4 — discoverability honesta para el kine durante el piloto.
// Ambas opcionales — el caller decide cuál mostrar (algunos contextos
// usan solo una). Si un schema tiene los 2, se renderean apilados.
//
// Default semántico cuando los campos del schema están ausentes:
//   - clinical_review_status ausente → "preliminary" (conservador).
//   - tracking_mode ausente → "camera" (no muestra el badge gris).

import type { ExerciseSchema } from "@/lib/pose/types";

interface Props {
  schema: ExerciseSchema | undefined;
  /** Por default muestra ambos. Pasá false para suprimir alguno
   *  individual (ej. solo "Sin detección de cámara" en PrescribirRutina
   *  si la calibración no es relevante en ese contexto). */
  showCalibracion?: boolean;
  showSinCamara?: boolean;
}

export function ExerciseStatusBadges({
  schema,
  // Showcase (02/10/2026): apagado por default, se veía como "beta sin
  // validar" en el demo. Volver a true si el proyecto se reactiva.
  showCalibracion = false,
  showSinCamara = true,
}: Props) {
  if (!schema) return null;

  const isPreliminary =
    (schema.clinical_review_status ?? "preliminary") === "preliminary";
  const isVideoOnly = schema.tracking_mode === "video_only";

  const showPrelim = showCalibracion && isPreliminary;
  const showSin = showSinCamara && isVideoOnly;

  if (!showPrelim && !showSin) return null;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {showPrelim && (
        <span
          className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 rounded-full"
          title="Los thresholds de detección de este ejercicio están calibrados empíricamente pero no fueron validados clínicamente todavía."
        >
          Calibración preliminar
        </span>
      )}
      {showSin && (
        <span
          className="text-xs px-2 py-0.5 bg-slate-100 text-slate-700 border border-slate-300 rounded-full"
          title="Este ejercicio se ejecuta con video tutorial — el paciente marca manualmente cuando termina. Sin medición de pose."
        >
          Sin detección de cámara
        </span>
      )}
    </div>
  );
}
